import { useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { Numeric, Tooltip } from '@blade/ui-kit'
import {
  PairAuditBadge,
  PoolLiquidityBadge,
} from '@root/entities/pair/ui/common'
import { showAllTimeHighChartLineAtom } from '@root/features/trade/trading'
import {
  formatMarketCapShort,
  MarkerCapShortFormatted,
} from '@root/shared/core/format'
import { useTranslation } from '@root/shared/localization'
import { Separator } from '@root/shared/ui-kit'

import { PairInformationPoolItem } from './trading-pair-pool-info-item'

import type { PairModel } from '@root/entities/pair'
import type { ClassNameProps } from '@root/shared/react'

type TradingPairPoolInfoProps = {
  pair: PairModel
  onAuditClick?: () => void
} & ClassNameProps

export const TradingPairPoolInfo: React.FC<TradingPairPoolInfoProps> = ({
  className,
  pair,
  onAuditClick,
}) => {
  const { t } = useTranslation()
  const [price] = useAtom(pair.price)
  const [marketCapUsd] = useAtom(pair.marketCapUsd)
  const [athMarketCap] = useAtom(pair.athMarketCap)
  const [liquidityUsd] = useAtom(pair.liquidityUsd)
  const [audit] = useAtom(pair.audit)
  const [_, setShowAllTimeHighChartLine] = useAtom(showAllTimeHighChartLineAtom)

  return (
    <Root className={className}>
      <Item title={t('common.price')}>
        <Numeric
          value={price.priceUsd}
          preset="currency-amount-short"
          currency="$"
        />
      </Item>
      <Separator vertical />
      <Item title={t('common.mkt_cap')}>
        <Tooltip
          content={t('common.ath_mkt_cap', {
            value: formatMarketCapShort(athMarketCap),
          })}
        >
          <Mcap
            onMouseEnter={() => setShowAllTimeHighChartLine(true)}
            onMouseLeave={() => setShowAllTimeHighChartLine(false)}
          >
            <MarkerCapShortFormatted mcap={marketCapUsd} />
          </Mcap>
        </Tooltip>
      </Item>
      <Separator vertical />
      <Item title={t('common.liquidity')}>
        <PoolLiquidityBadge data={audit.checks.lpBurned} />
        <Numeric
          value={liquidityUsd}
          preset="currency-amount-short"
          currency="$"
        />
      </Item>
      <Separator vertical />
      <Item title={t('common.supply')}>
        <Numeric value={pair.quote.supply} preset="currency-amount-short" />
      </Item>
      <Separator vertical />
      <Item title={t('common.audit')}>
        <PairAuditBadge audit={audit} onAuditClick={onAuditClick} />
      </Item>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: repeat(4, 1fr 1px) 1fr;
  align-items: center;
  justify-items: start;

  height: 50px;
  padding-inline: 12px;
  background: var(--background-1);
  border-bottom: 1px solid var(--background-3);
`

const Mcap = styled.div`
  display: flex;
  align-items: center;
`

const Item = styled(PairInformationPoolItem)`
  padding-inline: 12px;

  &:last-child {
    padding-inline-end: 0;
  }
  &:first-child {
    padding-inline-start: 0;
  }
`
